// Copyright 2023 The Casdoor Authors. All Rights Reserved.
//
// Licensed under the Apache License, Version 2.0 (the "License");
// you may not use this file except in compliance with the License.
// You may obtain a copy of the License at
//
//      http://www.apache.org/licenses/LICENSE-2.0
//
// Unless required by applicable law or agreed to in writing, software
// distributed under the License is distributed on an "AS IS" BASIS,
// WITHOUT WARRANTIES OR CONDITIONS OF ANY KIND, either express or implied.
// See the License for the specific language governing permissions and
// limitations under the License.

import i18next from 'i18next'
import React from 'react'
import { CheckCircleTwoTone, CloseCircleTwoTone } from '@ant-design/icons'

function isValidOption_AtLeast6(password) {
  if (password.length < 6) {
    return i18next.t('user:The password must have at least 6 characters')
  }
  return ''
}

const isUSACustomers = (user = {}) => {
  const { properties = {}, type } = user
  return (
    ['840', '630', '316'].includes(properties.regionCode) && type !== 'employee'
  )
}
function isValidOption_AtLeast8(password, user) {
  if (isUSACustomers(user) && password.length < 15) {
    return i18next.t('modification:15 characters')
  } else if (password.length < 8) {
    return i18next.t('modification:8 characters')
  }
  return ''
}

function isValidOption_UpperCase(password) {
  const regex = /^(?=.*[A-Z]).+$/
  if (!regex.test(password)) {
    return i18next.t('modification:1 uppercase (A-Z)')
  }
  return ''
}
function isValidOption_LowerCase(password) {
  const regex = /^(?=.*[a-z]).+$/
  if (!regex.test(password)) {
    return i18next.t('modification:1 lowercase (a-z)')
  }
  return ''
}
function isValidOption_Number(password) {
  const regex = /^(?=.*[0-9]).+$/
  if (!regex.test(password)) {
    return i18next.t('modification:1 number (0-9)')
  }
  return ''
}

function isValidOption_SpecialChar(password) {
  const regex = /^(?=.*[!-/:-@[-`{-~]).+$/
  if (!regex.test(password)) {
    return i18next.t(
      'user:The password must contain at least one special character'
    )
  }
  return ''
}

function isValidOption_NoRepeat(password) {
  const regex = /(.)\1+/
  if (regex.test(password)) {
    return i18next.t(
      'user:The password must not contain any repeated characters'
    )
  }
  return ''
}

const checkers = {
  AtLeast6: isValidOption_AtLeast6,
  AtLeast8: isValidOption_AtLeast8,
  Aa123: [
    isValidOption_UpperCase,
    isValidOption_LowerCase,
    isValidOption_Number
  ],
  SpecialChar: isValidOption_SpecialChar,
  NoRepeat: isValidOption_NoRepeat
}

export function renderPasswordPopover(options, password, user) {
  const checkerOptions = options.map(option => checkers[option]).flat()
  return (
    <div style={{ width: 220 }}>
      <div style={{ fontWeight: 'bold', marginBottom: '5px' }}>
        {i18next.t('modification:Password must contain at least')}:
      </div>
      {checkerOptions.map((checker, idx) => {
        return (
          <div key={idx}>
            {checker(password, user) === '' ? (
              <CheckCircleTwoTone twoToneColor={'#52c41a'} />
            ) : (
              <CloseCircleTwoTone twoToneColor={'#ff4d4f'} />
            )}{' '}
            {checker('', user)}
          </div>
        )
      })}
    </div>
  )
}

export function checkPasswordComplexity(password, options, user) {
  if (password.length === 0) {
    return i18next.t('login:Please input your password!')
  }

  if (!options || options.length === 0) {
    return ''
  }

  for (const option of options) {
    const checkerFunc = checkers[option]
    if (checkerFunc) {
      if (Array.isArray(checkerFunc)) {
        for (const checker of checkerFunc) {
          const errorMsg = checker(password, user)
          if (errorMsg !== '') {
            return errorMsg
          }
        }
      } else {
        const errorMsg = checkerFunc(password, user)
        if (errorMsg !== '') {
          return errorMsg
        }
      }
    }
  }
  return ''
}
